'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import { Send } from 'lucide-react';
import { SUGGESTED_QUESTIONS } from '@/lib/constants';
import { useChatStore } from '@/stores/chatStore';

const BibleScene = dynamic(
  () => import('@/components/three/BibleScene'),
  { ssr: false }
);

export default function HomePage() {
  const [question, setQuestion] = useState('');
  const router = useRouter();
  const sendMessage = useChatStore((s) => s.sendMessage);

  const handleSubmit = (text?: string) => {
    const q = (text || question).trim();
    if (!q) return;
    sendMessage(q);
    router.push('/chat');
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{
        background:
          'radial-gradient(ellipse at center top, #121829 0%, #0a0e1a 60%)',
      }}
    >
      {/* 3D Bible hero */}
      <div className="relative" style={{ height: '60vh' }}>
        <BibleScene />

        {/* Gradient overlay at bottom */}
        <div
          className="absolute bottom-0 left-0 right-0 h-32 pointer-events-none"
          style={{
            background:
              'linear-gradient(to bottom, transparent, #0a0e1a)',
          }}
        />
      </div>

      {/* Content area */}
      <div className="flex-1 flex flex-col items-center px-6 -mt-8 relative z-10">
        {/* Title */}
        <h1
          className="text-4xl md:text-5xl font-bold mb-3 text-center"
          style={{ color: 'var(--text-primary)' }}
        >
          Bible
          <span style={{ color: 'var(--accent-gold)' }}>LLM</span>
        </h1>
        <p
          className="text-sm mb-8 text-center max-w-md"
          style={{ color: 'var(--text-secondary)' }}
        >
          AI-powered Bible exploration. Ask questions, search scripture, and
          discover connections — all grounded in actual verses.
        </p>

        {/* ChatGPT-style input */}
        <div
          className="w-full max-w-2xl flex items-center gap-3 px-5 py-4 rounded-xl mb-6"
          style={{
            backgroundColor: 'var(--bg-card)',
            border: '1px solid rgba(200, 164, 94, 0.2)',
          }}
        >
          <input
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder="Ask a question about the Bible..."
            className="flex-1 bg-transparent text-base outline-none placeholder:text-white/25"
            style={{ color: 'var(--text-primary)' }}
          />
          <button
            onClick={() => handleSubmit()}
            disabled={!question.trim()}
            className="p-2 rounded-lg transition-all duration-200 disabled:opacity-30"
            style={{
              backgroundColor: question.trim()
                ? 'var(--accent-gold)'
                : 'transparent',
              color: question.trim()
                ? 'var(--bg-primary)'
                : 'var(--text-secondary)',
            }}
          >
            <Send size={18} />
          </button>
        </div>

        {/* Suggested questions */}
        <div className="flex flex-wrap gap-2 justify-center max-w-2xl">
          {SUGGESTED_QUESTIONS.map((q) => (
            <button
              key={q}
              onClick={() => handleSubmit(q)}
              className="text-xs px-3 py-1.5 rounded-full transition-all duration-200"
              style={{
                backgroundColor: 'rgba(200, 164, 94, 0.08)',
                color: 'var(--text-secondary)',
                border: '1px solid rgba(200, 164, 94, 0.12)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor =
                  'rgba(200, 164, 94, 0.3)';
                e.currentTarget.style.color = 'var(--accent-gold)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor =
                  'rgba(200, 164, 94, 0.12)';
                e.currentTarget.style.color = 'var(--text-secondary)';
              }}
            >
              {q}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
